
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BarChart2, 
  Users, 
  Zap, 
  Shield, 
  ArrowRight, 
  CheckCircle,
  TrendingUp,
  MessageCircle,
  CreditCard,
  Star,
  ChevronDown,
  ChevronUp,
  Layout,
  Activity,
  PieChart
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const FeatureCard = ({ icon: Icon, title, description }) => (
  <motion.div 
    whileHover={{ y: -5 }}
    className="p-6 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm hover:bg-white/10 transition-all duration-300"
  >
    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-green-500/20 to-emerald-500/20 flex items-center justify-center mb-4">
      <Icon className="text-green-400" size={24} />
    </div>
    <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
    <p className="text-slate-400 leading-relaxed">{description}</p>
  </motion.div>
);

const StepCard = ({ number, title, description }) => (
  <div className="relative pl-8 pb-12 border-l border-white/10 last:border-0 last:pb-0">
    <div className="absolute left-[-16px] top-0 w-8 h-8 rounded-full bg-[#09090b] border border-green-500/50 flex items-center justify-center text-green-400 font-bold text-sm shadow-[0_0_10px_rgba(34,197,94,0.2)]">
      {number}
    </div>
    <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
    <p className="text-slate-400">{description}</p>
  </div>
);

const TestimonialCard = ({ name, role, text, avatar }) => (
  <div className="p-6 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm">
    <div className="flex items-center gap-1 mb-4 text-yellow-500">
      {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="currentColor" />)}
    </div>
    <p className="text-slate-300 mb-6 leading-relaxed">"{text}"</p>
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-white font-bold text-sm">
        {name[0]}
      </div>
      <div>
        <div className="font-bold text-white text-sm">{name}</div>
        <div className="text-slate-500 text-xs">{role}</div>
      </div>
    </div>
  </div>
);

const FaqItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <div className="border-b border-white/10 last:border-0">
      <button 
        className="w-full py-4 flex items-center justify-between text-left focus:outline-none"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="font-medium text-white">{question}</span>
        {isOpen ? <ChevronUp size={20} className="text-slate-500" /> : <ChevronDown size={20} className="text-slate-500" />}
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="pb-4 text-slate-400 text-sm leading-relaxed">
              {answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const DashboardPreview = () => (
  <div className="rounded-xl border border-white/10 bg-[#09090b]/80 backdrop-blur-xl shadow-2xl overflow-hidden">
    {/* Mock Header */}
    <div className="h-12 border-b border-white/10 flex items-center px-4 gap-4">
      <div className="flex gap-1.5">
        <div className="w-3 h-3 rounded-full bg-red-500/50" />
        <div className="w-3 h-3 rounded-full bg-yellow-500/50" />
        <div className="w-3 h-3 rounded-full bg-green-500/50" />
      </div>
      <div className="h-2 w-32 bg-white/10 rounded-full ml-4" />
    </div>
    {/* Mock Content */}
    <div className="p-6 grid grid-cols-3 gap-6">
      {/* Sidebar */}
      <div className="col-span-1 space-y-4 hidden md:block">
        <div className="h-8 w-3/4 bg-white/10 rounded-lg mb-6" />
        <div className="space-y-2">
          <div className="h-8 w-full bg-green-500/20 rounded-lg border border-green-500/30" />
          <div className="h-8 w-full bg-white/5 rounded-lg" />
          <div className="h-8 w-full bg-white/5 rounded-lg" />
          <div className="h-8 w-full bg-white/5 rounded-lg" />
        </div>
      </div>
      {/* Main Area */}
      <div className="col-span-3 md:col-span-2 space-y-6">
        {/* Top Cards */}
        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 rounded-xl bg-white/5 border border-white/10">
            <div className="flex justify-between mb-2">
              <div className="h-2 w-12 bg-white/20 rounded-full" />
              <Activity size={16} className="text-green-400" />
            </div>
            <div className="h-6 w-20 bg-white rounded mb-1" />
            <div className="h-2 w-10 bg-green-500 rounded-full" />
          </div>
          <div className="p-4 rounded-xl bg-white/5 border border-white/10">
            <div className="flex justify-between mb-2">
              <div className="h-2 w-12 bg-white/20 rounded-full" />
              <Users size={16} className="text-blue-400" />
            </div>
            <div className="h-6 w-20 bg-white rounded mb-1" />
            <div className="h-2 w-10 bg-blue-500 rounded-full" />
          </div>
        </div>
        {/* Chart Area */}
        <div className="p-4 rounded-xl bg-white/5 border border-white/10 h-40 flex items-end justify-between gap-2">
          {[40, 60, 45, 70, 50, 80, 65, 85, 75, 90, 60, 95].map((h, i) => (
            <div key={i} className="w-full bg-green-500/30 rounded-t-sm hover:bg-green-500/50 transition-colors" style={{ height: `${h}%` }} />
          ))}
        </div>
        {/* Table Area */}
        <div className="rounded-xl border border-white/10 overflow-hidden">
          <div className="h-10 bg-white/5 border-b border-white/10" />
          <div className="p-4 space-y-3">
            <div className="flex items-center gap-4"><div className="w-8 h-8 rounded-full bg-white/10" /><div className="h-3 w-24 bg-white/10 rounded" /><div className="h-3 w-12 bg-white/10 rounded ml-auto" /></div>
            <div className="flex items-center gap-4"><div className="w-8 h-8 rounded-full bg-white/10" /><div className="h-3 w-20 bg-white/10 rounded" /><div className="h-3 w-12 bg-white/10 rounded ml-auto" /></div>
            <div className="flex items-center gap-4"><div className="w-8 h-8 rounded-full bg-white/10" /><div className="h-3 w-28 bg-white/10 rounded" /><div className="h-3 w-12 bg-white/10 rounded ml-auto" /></div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const PricingCard = ({ title, price, features, recommended = false, onAction }) => (
  <div className={`p-8 rounded-3xl relative flex flex-col h-full ${recommended ? 'bg-gradient-to-b from-green-600/20 to-emerald-900/20 border border-green-500/50' : 'bg-white/5 border border-white/10'}`}>
    {recommended && (
      <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full text-xs font-bold text-white uppercase tracking-wider">
        Рекомендуем
      </div>
    )}
    <h3 className="text-lg font-medium text-slate-300 mb-2">{title}</h3>
    <div className="text-4xl font-bold text-white mb-6">
      {price} <span className="text-lg text-slate-500 font-normal">/мес</span>
    </div>
    <ul className="space-y-4 mb-8 flex-1">
      {features.map((feature, idx) => (
        <li key={idx} className="flex items-start gap-3 text-slate-300">
          <CheckCircle className="text-green-400 shrink-0 mt-0.5" size={18} />
          <span className="text-sm">{feature}</span>
        </li>
      ))}
    </ul>
    <button 
      onClick={onAction}
      className={`w-full py-3 rounded-xl font-bold transition-all ${recommended ? 'bg-green-600 hover:bg-green-500 text-white shadow-lg shadow-green-500/25' : 'bg-white/10 hover:bg-white/20 text-white'}`}
    >
      Начать сейчас
    </button>
  </div>
);

export default function Landing() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const handleAction = () => {
    if (token) {
      navigate('/dashboard');
    } else {
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] text-white selection:bg-green-500/30 overflow-x-hidden">
      {/* Navbar */}
      <nav className="fixed top-0 w-full z-50 bg-[#09090b]/80 backdrop-blur-md border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center font-bold text-white">
              L
            </div>
            <span className="font-bold text-xl tracking-tight">LuckStat</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-sm font-medium text-slate-400 hover:text-white transition-colors">Возможности</a>
            <a href="#pricing" className="text-sm font-medium text-slate-400 hover:text-white transition-colors">Тарифы</a>
            <button 
              onClick={handleAction}
              className="px-4 py-2 bg-white/10 hover:bg-white/15 rounded-lg text-sm font-medium transition-colors"
            >
              {token ? 'Дашборд' : 'Войти'}
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden">
        {/* Background Gradients */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-green-600/20 rounded-full blur-[120px] -z-10" />
        <div className="absolute bottom-0 right-0 w-[800px] h-[600px] bg-emerald-600/10 rounded-full blur-[100px] -z-10" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 mb-8">
              <span className="flex h-2 w-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-xs font-medium text-slate-300">Аналитика Telegram каналов 2.0</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-8 leading-tight">
              Управляйте ростом <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 via-emerald-400 to-teal-400">
                вашей Telegram империи
              </span>
            </h1>
            <p className="text-lg md:text-xl text-slate-400 max-w-2xl mx-auto mb-12 leading-relaxed">
              Мощная аналитика, отслеживание пути пользователя и автоматизация продаж в одном инструменте. 
              Превратите подписчиков в лояльных клиентов.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button 
                onClick={handleAction}
                className="px-8 py-4 bg-green-600 hover:bg-green-500 text-white rounded-xl font-bold text-lg shadow-lg shadow-green-500/25 transition-all w-full sm:w-auto flex items-center justify-center gap-2 group"
              >
                {token ? 'Перейти в Дашборд' : 'Начать бесплатно'}
                <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
              </button>
              <button className="px-8 py-4 bg-white/5 hover:bg-white/10 text-white rounded-xl font-bold text-lg border border-white/10 transition-all w-full sm:w-auto">
                Узнать больше
              </button>
            </div>
          </motion.div>

          {/* Dashboard Preview Mockup */}
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-20 relative mx-auto max-w-5xl"
          >
            <DashboardPreview />
          </motion.div>
        </div>
      </section>

      {/* How it works Section */}
      <section className="py-24 relative bg-white/[0.02] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Как это работает</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Всего 3 простых шага, чтобы начать получать инсайты о вашей аудитории.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <StepCard 
                number="1"
                title="Подключите канал"
                description="Добавьте бота в администраторы вашего канала. Это безопасно и занимает 1 минуту."
              />
              <StepCard 
                number="2"
                title="Получите данные"
                description="Мы автоматически соберем исторические данные и начнем отслеживать новые события."
              />
              <StepCard 
                number="3"
                title="Анализируйте и растите"
                description="Используйте полученные инсайты для улучшения контента и настройки рекламных кампаний."
              />
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-green-500/20 blur-[100px] rounded-full" />
              <div className="relative rounded-2xl border border-white/10 bg-[#09090b]/80 backdrop-blur-xl p-6 shadow-2xl">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                    <Zap className="text-green-400" size={24} />
                  </div>
                  <div>
                    <div className="font-bold text-white">Быстрый старт</div>
                    <div className="text-xs text-slate-400">Настройка за 2 минуты</div>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="h-2 w-full bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full w-2/3 bg-green-500" />
                  </div>
                  <div className="flex justify-between text-xs text-slate-400">
                    <span>Подключение...</span>
                    <span className="text-green-400">66%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-24 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Всё необходимое для роста</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Мы объединили лучшие инструменты аналитики и маркетинга, чтобы вы могли сосредоточиться на контенте.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <FeatureCard 
              icon={BarChart2}
              title="Глубокая аналитика"
              description="Отслеживайте источники трафика, конверсии и поведение пользователей в реальном времени."
            />
            <FeatureCard 
              icon={Users}
              title="Путь пользователя"
              description="Понимайте, как подписчики взаимодействуют с вашим контентом от первого клика до покупки."
            />
            <FeatureCard 
              icon={Zap}
              title="Мгновенные уведомления"
              description="Получайте оповещения о важных событиях: новых подписках, продажах и отписках."
            />
            <FeatureCard 
              icon={Shield}
              title="Безопасность данных"
              description="Ваши данные надежно зашифрованы и доступны только вам. Мы ценим приватность."
            />
            <FeatureCard 
              icon={TrendingUp}
              title="Прогнозирование"
              description="Используйте исторические данные для прогнозирования роста и планирования кампаний."
            />
            <FeatureCard 
              icon={CreditCard}
              title="Управление продажами"
              description="Интеграция с платежными системами и отслеживание LTV каждого клиента."
            />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 border-y border-white/5 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-white mb-2">500+</div>
              <div className="text-sm text-slate-500 font-medium uppercase tracking-wider">Каналов подключено</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-white mb-2">1M+</div>
              <div className="text-sm text-slate-500 font-medium uppercase tracking-wider">Подписчиков отслежено</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-white mb-2">$500k</div>
              <div className="text-sm text-slate-500 font-medium uppercase tracking-wider">Продаж обработано</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-white mb-2">99.9%</div>
              <div className="text-sm text-slate-500 font-medium uppercase tracking-wider">Uptime</div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[500px] bg-green-600/5 rounded-full blur-[120px] -z-10" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Что говорят пользователи</h2>
            <p className="text-slate-400">
              Присоединяйтесь к сотням довольных администраторов каналов.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <TestimonialCard 
              name="Алексей Смирнов"
              role="Админ 'Крипто Инсайт'"
              text="Наконец-то я вижу, откуда приходят подписчики и сколько денег они приносят. ROI вырос на 40% за первый месяц."
            />
            <TestimonialCard 
              name="Мария Иванова"
              role="Продюсер запусков"
              text="LuckStat заменил нам 3 разных сервиса. Очень удобно отслеживать воронки продаж прямо внутри одного дашборда."
            />
            <TestimonialCard 
              name="Дмитрий Волков"
              role="Маркетолог"
              text="Лучшая аналитика для Telegram на рынке. Особенно нравится детализация по каждому пользователю."
            />
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Простые тарифы</h2>
            <p className="text-slate-400">
              Начните бесплатно, платите по мере роста. Никаких скрытых платежей.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <PricingCard 
              title="Старт"
              price="0 ₽"
              features={[
                "1 Telegram канал",
                "Базовая аналитика",
                "История за 7 дней",
                "До 1000 подписчиков"
              ]}
              onAction={handleAction}
            />
            <PricingCard 
              title="Pro"
              price="1 000 ₽"
              recommended={true}
              features={[
                "До 10 Telegram каналов",
                "Полная аналитика",
                "История за 1 год",
                "Экспорт данных",
                "Приоритетная поддержка"
              ]}
              onAction={handleAction}
            />
            <PricingCard 
              title="Бизнес"
              price="4 900 ₽"
              features={[
                "Безлимитные каналы",
                "API доступ",
                "Персональный менеджер",
                "White Label отчеты",
                "SLA гарантия"
              ]}
              onAction={handleAction}
            />
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 border-t border-white/5 bg-white/[0.02]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Частые вопросы</h2>
            <p className="text-slate-400">
              Всё, что вы хотели знать о работе сервиса.
            </p>
          </div>
          <div className="space-y-2">
            <FaqItem 
              question="Безопасно ли давать права администратора боту?"
              answer="Абсолютно. Бот запрашивает только минимально необходимые права для чтения статистики и отслеживания вступлений. Мы не можем публиковать сообщения от вашего имени или удалять подписчиков без вашего ведома."
            />
            <FaqItem 
              question="Как работает бесплатный период?"
              answer="На тарифе 'Старт' вы можете пользоваться базовыми функциями бесплатно и бессрочно. Для доступа к расширенной аналитике вы можете активировать пробный период Pro тарифа на 14 дней."
            />
            <FaqItem 
              question="Можно ли отменить подписку?"
              answer="Да, вы можете отменить подписку в любой момент в личном кабинете. Оплаченный период останется активным до конца срока действия."
            />
            <FaqItem 
              question="Есть ли скидки при оплате за год?"
              answer="Да, при оплате за год вы получаете скидку 20% на любой платный тариф."
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-green-900/20 to-[#09090b]" />
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Готовы увеличить доход канала?</h2>
          <p className="text-xl text-slate-400 mb-10">
            Присоединяйтесь к LuckStat сегодня и начните принимать решения на основе данных.
          </p>
          <button 
            onClick={handleAction}
            className="px-10 py-5 bg-white text-black hover:bg-slate-200 rounded-xl font-bold text-lg shadow-xl shadow-white/10 transition-all transform hover:scale-105"
          >
            {token ? 'Перейти в Дашборд' : 'Создать аккаунт бесплатно'}
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-white/5 bg-[#050507]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-6 h-6 rounded bg-green-600 flex items-center justify-center font-bold text-xs">L</div>
                <span className="font-bold text-lg">LuckStat</span>
              </div>
              <p className="text-slate-500 text-sm max-w-xs">
                Современная платформа для аналитики и монетизации Telegram каналов.
                Создано для админов, маркетологов и продюсеров.
              </p>
            </div>
            <div>
              <h4 className="font-bold text-white mb-4">Продукт</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><a href="#" className="hover:text-white transition-colors">Возможности</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Тарифы</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Интеграции</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Roadmap</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-white mb-4">Компания</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><a href="#" className="hover:text-white transition-colors">О нас</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Блог</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Контакты</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-slate-600 text-xs">
              © 2024 LuckStat. All rights reserved.
            </div>
            <div className="flex gap-6">
               {/* Social icons could go here */}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
